import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Send, Trash2, Shield, MapPin, Calendar, Clock, Users, Trophy, Crown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { ddmmyyyy } from "../lib/dates";
import { fmt12 } from "../components/TimePicker12";

export default function CommunityGame() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    const [post, setPost] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const bottomRef = useRef(null);

    const loadPost = async () => {
        const { data } = await api.get(`/finder/posts/${id}`);
        setPost(data);
        return data;
    };
    const loadMessages = async (postData) => {
        const p = postData || post;
        if (!(p?.is_host || p?.is_joiner || isAdmin)) {
            setMessages([]);
            return;
        }
        try {
            const { data } = await api.get(`/finder/posts/${id}/messages`);
            setMessages(data);
        } catch { /* not allowed yet */ }
    };

    useEffect(() => {
        (async () => {
            const p = await loadPost();
            await loadMessages(p);
        })();
        const t = setInterval(() => loadMessages(), 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

    const join = async () => {
        try {
            const { data } = await api.post(`/finder/posts/${id}/join`);
            toast.success(`Joined! Host contact: ${data.creator_phone || "shared via app"}`);
            const p = await loadPost();
            await loadMessages(p);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const leave = async () => {
        if (!confirm("Leave this game?")) return;
        try {
            await api.post(`/finder/posts/${id}/leave`);
            toast.success("You left the game");
            const p = await loadPost();
            await loadMessages(p);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const remove = async () => {
        if (!confirm("Delete this game?")) return;
        try {
            await api.delete(`/finder/posts/${id}`);
            toast.success("Game deleted");
            navigate("/community");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const send = async (e) => {
        e?.preventDefault();
        const t = text.trim();
        if (!t) return;
        try {
            await api.post(`/finder/posts/${id}/messages`, { content: t });
            setText("");
            loadMessages();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const deleteMsg = async (msgId) => {
        if (!confirm("Delete this message?")) return;
        try {
            await api.delete(`/finder/posts/${id}/messages/${msgId}`);
            loadMessages();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    if (!post) return <div className="max-w-3xl mx-auto p-6 text-slate-500">Loading...</div>;
    const joinersCount = (post.joiners || []).length;
    const filled = joinersCount + 1; // +1 = host counts
    const spotsLeft = Math.max(0, post.players_needed - filled);
    const canChat = post.is_host || post.is_joiner || isAdmin;
    const full = spotsLeft === 0;

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex flex-col h-[calc(100vh-72px)]">
            <Toaster theme="dark" richColors position="top-center" />

            {/* Top bar */}
            <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-800 flex-shrink-0">
                <button onClick={() => navigate("/community")} className="text-slate-400 hover:text-[#00FF66]" data-testid="back-community">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">{post.sport}</span>
                        {post.price_per_share > 0 && (
                            <span data-testid="game-detail-price"
                                className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                ₹{post.price_per_share} / player
                            </span>
                        )}
                        {full && <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 border border-slate-600">Full</span>}
                    </div>
                    <div className="font-display font-bold uppercase text-white mt-1 truncate" data-testid="game-detail-title">
                        {post.user_name}'s {post.sport} game
                    </div>
                    <div className="text-xs text-slate-500 truncate">{post.centre_name} · {ddmmyyyy(post.post_date)} · {fmt12(post.start_time)}{post.end_time ? ` – ${fmt12(post.end_time)}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                        <div className="font-display text-2xl font-bold text-[#00FF66] leading-none" data-testid="game-detail-spots">{spotsLeft}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">spots left</div>
                    </div>
                </div>
            </div>

            {/* Main content: 2 columns */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden mt-4">
                {/* Left — Players + meta */}
                <div className="md:col-span-1 overflow-y-auto space-y-4 pb-4">
                    {post.note && (
                        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 text-sm text-slate-300">{post.note}</div>
                    )}
                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 space-y-2 text-xs">
                        <Row icon={MapPin} label="Centre" value={post.centre_name} />
                        <Row icon={Calendar} label="Date" value={ddmmyyyy(post.post_date)} />
                        <Row icon={Clock} label="Time" value={`${fmt12(post.start_time)}${post.end_time ? ` – ${fmt12(post.end_time)}` : ""}`} />
                        <Row icon={Users} label="Players" value={`${filled} / ${post.players_needed}`} />
                        {post.price_per_share > 0 && (
                            <Row icon={Trophy} label="Per player" value={`₹${post.price_per_share}`} accent />
                        )}
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="players-list">
                        <div className="px-3 py-2 border-b border-slate-800 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">
                            Players · {filled}
                        </div>
                        <div className="divide-y divide-slate-800">
                            {/* Host */}
                            <div className="px-3 py-2 flex items-center gap-2" data-testid="player-host">
                                <Avatar name={post.user_name} highlight />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white font-bold truncate">{post.user_name}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-amber-300 inline-flex items-center gap-1">
                                        <Crown className="w-3 h-3" /> Host
                                    </div>
                                </div>
                            </div>
                            {(post.joiners || []).map(j => (
                                <div key={j.user_id} className="px-3 py-2 flex items-center gap-2" data-testid={`player-${j.user_id}`}>
                                    <Avatar name={j.name} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-slate-200 truncate">{j.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Joined</div>
                                    </div>
                                </div>
                            ))}
                            {(post.joiners || []).length === 0 && (
                                <div className="px-3 py-3 text-xs text-slate-500 text-center">No one has joined yet.</div>
                            )}
                        </div>
                    </div>

                    {/* Action button */}
                    <div>
                        {post.is_host ? (
                            <button onClick={remove} data-testid="game-delete-btn"
                                className="w-full h-10 inline-flex items-center justify-center gap-2 bg-red-950/40 border border-red-900/60 text-red-300 rounded text-xs font-bold uppercase tracking-widest hover:bg-red-950">
                                <Trash2 className="w-4 h-4" /> Delete game
                            </button>
                        ) : post.is_joiner ? (
                            <button onClick={leave} data-testid="game-leave-btn"
                                className="w-full h-10 inline-flex items-center justify-center gap-2 border border-amber-700/60 text-amber-300 rounded text-xs font-bold uppercase tracking-widest hover:bg-amber-950/30">
                                Leave game
                            </button>
                        ) : (
                            <button onClick={join} disabled={full} data-testid="game-join-btn"
                                className="w-full h-10 bg-[#00FF66] text-black rounded text-xs font-bold uppercase tracking-widest hover:bg-[#00cc52] disabled:opacity-40">
                                {full ? "Game full" : "Join game"}
                            </button>
                        )}
                    </div>
                </div>

                {/* Right — Chat */}
                <div className="md:col-span-2 flex flex-col bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                    <div className="px-4 py-2 border-b border-slate-800 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 flex-shrink-0">
                        Game Chat · only host + joiners
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="game-messages">
                        {!canChat ? (
                            <div className="text-center text-slate-500 text-sm py-8">Join the game to access the chat.</div>
                        ) : messages.length === 0 ? (
                            <div className="text-center text-slate-500 text-sm py-8">No messages yet — say hi 👋</div>
                        ) : messages.map(m => (
                            <Message key={m.id} m={m} self={m.user_id === user?.id}
                                canModerate={isAdmin || m.user_id === user?.id}
                                onDelete={() => deleteMsg(m.id)} />
                        ))}
                        <div ref={bottomRef} />
                    </div>
                    {canChat && (
                        <form onSubmit={send} className="border-t border-slate-800 p-2 flex gap-2 flex-shrink-0">
                            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message…"
                                data-testid="game-chat-input"
                                className="flex-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            <button type="submit" disabled={!text.trim()} data-testid="game-chat-send"
                                className="w-10 h-10 flex items-center justify-center bg-[#00FF66] text-black rounded hover:bg-[#00cc52] disabled:opacity-40">
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

function Avatar({ name, highlight }) {
    return (
        <div className={`w-8 h-8 rounded-full font-display font-bold text-xs flex items-center justify-center flex-shrink-0 ${
            highlight ? "bg-amber-300 text-slate-950 border-2 border-amber-200" : "bg-[#00FF66] text-black"
        }`}>
            {(name || "?").charAt(0).toUpperCase()}
        </div>
    );
}

function Row({ icon: Icon, label, value, accent }) {
    return (
        <div className="flex items-center gap-2">
            <Icon className={`w-3 h-3 ${accent ? "text-amber-300" : "text-[#00FF66]"} flex-shrink-0`} />
            <span className="text-slate-500 text-[10px] uppercase tracking-widest">{label}</span>
            <span className={`ml-auto font-bold ${accent ? "text-amber-300" : "text-white"} truncate`}>{value}</span>
        </div>
    );
}

function Message({ m, self, canModerate, onDelete }) {
    const isAdmin = m.user_role === "admin" || m.user_role === "super_admin";
    const time = new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return (
        <div className={`flex ${self ? "justify-end" : "justify-start"}`} data-testid={`game-msg-${m.id}`}>
            <div className={`max-w-[80%] group relative ${self ? "ml-12" : "mr-12"}`}>
                <div className={`flex items-center gap-2 mb-1 ${self ? "justify-end" : ""}`}>
                    <span className="font-bold text-xs text-slate-200">{self ? "You" : m.user_name}</span>
                    {isAdmin && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-pink-500/20 text-pink-300 border border-pink-500/40">
                            <Shield className="w-2.5 h-2.5" /> Admin
                        </span>
                    )}
                    <span className="text-[10px] text-slate-500">{time}</span>
                </div>
                <div className={`rounded-2xl px-4 py-2 ${
                    self ? "bg-[#00FF66]/15 border border-[#00FF66]/40 text-white" : "bg-slate-950 border border-slate-800 text-slate-100"
                }`}>
                    <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                </div>
                {canModerate && (
                    <button onClick={onDelete} data-testid={`del-game-msg-${m.id}`}
                        className="absolute top-0 -right-7 opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition">
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
